/* 
  @Author Beae
  @Description Slider vendor
  @Version 1.0
*/
if (!window.beae_slider) {
  window.beae_slider = (wrapper) => {
    // =====================
    // Helper: Check if autoplay is allowed (read-only from parent window)
    // Returns true if disabledInspector is true (inspector disabled = autoplay allowed)
    // =====================
    function isAutoplayAllowed() {
      try {
        if (window.parent && window.parent !== window && window.parent.ebEditorState) {
          return window.parent.ebEditorState.disabledInspector === true;
        }
        if (window.top && window.top !== window && window.top.ebEditorState) {
          return window.top.ebEditorState.disabledInspector === true;
        }
        return true;
      } catch (e) {
        return true;
      }
    }
    // =====================
    // Local State
    // =====================
    let embla = null;
    let dots = [];
    let autoplayTimeout = null;
    let autoplayDelay = 3000;
    let lastStartTime = null;
    let remainingTime = null;
    let boundEmblaNode = null;
    let progressValue = 0;
    let progressRAF = null;
    let isHovering = false;
    let stopAtEnd = false;
    let isLockedByChildDrag = false;
    function isDraggingActive(node) {
      return !!node.querySelector('[data-beae-dragging="true"]');
    }
    // =====================
    // Utils
    // =====================
    function parseTransition(value) {
      if (!value) return 3000;
      value = value.trim();
      if (value.endsWith("ms")) return parseFloat(value);
      if (value.endsWith("s")) return parseFloat(value) * 1000;
      return parseFloat(value);
    }
    function getItemsPerView(width, node) {
      const desktop = parseInt(node.getAttribute("data-item") || 1, 10);
      const tablet = parseInt(node.getAttribute("data-item-tablet") || 1, 10);
      const mobile = parseInt(node.getAttribute("data-item-mobile") || 1, 10);
      return width >= 1180 ? desktop : width >= 767.79 ? tablet : mobile;
    }
    function getPaginationViewWidth(node, defaultWidth = 80) {
      const raw = parseFloat(node.getAttribute("data-pagination-width"));
      return isNaN(raw) ? defaultWidth : raw;
    }
    function isSliderLayout(width, node) {
      const desktopLayout = node.getAttribute("data-desktop-layout");
      const tabletLayout = node.getAttribute("data-tablet-layout");
      const mobileLayout = node.getAttribute("data-mobile-layout");
    
      if (width >= 1180) {
        return desktopLayout === "slider";
      } else if (width >= 767.79) {
        const layout = tabletLayout || desktopLayout;
        return layout === "slider";
      } else {
        return mobileLayout === "slider";
      }
    }
    function blockInteractionWhenDragging(emblaNode) {
      const target = emblaNode.querySelector('.beae-slider-items') || emblaNode;
    
      const handler = (e) => {
        if (isDraggingActive(emblaNode)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
    
      target.addEventListener("wheel", handler, { passive: false });
      target.addEventListener("touchmove", handler, { passive: false });
      target.addEventListener("pointermove", handler, { passive: false });
    
      return () => {
        target.removeEventListener("wheel", handler);
        target.removeEventListener("touchmove", handler);
        target.removeEventListener("pointermove", handler);
      };
    }
    // =====================
    // Autoplay
    // =====================
    function resetProgress() {
      progressValue = 0;
      updateProgressBarUI(progressValue);
    }
    function animateProgressBar() {
      cancelAnimationFrame(progressRAF);
      const step = () => {
        if (!embla) return;
        const emblaNode = wrapper.querySelector(".beae-slider-wrapper");
        if (!emblaNode) return;
        const width = window.innerWidth;
        const isLoopEnabled = embla?.internalEngine()?.options?.loop;
        const selectedIndex = embla.selectedScrollSnap();
        const totalSlides = embla.slideNodes().length;
        const itemsPerView = getItemsPerView(width, emblaNode);
        const isAtLastSlide = selectedIndex >= totalSlides - itemsPerView;
        if (isHovering || stopAtEnd) {
          updateProgressBarUI(progressValue);
          progressRAF = requestAnimationFrame(step);
          return;
        }
        // time elapsed since last start
        const elapsed = Date.now() - lastStartTime;
        progressValue = Math.min(((elapsed) / autoplayDelay) * 100, 100);
        updateProgressBarUI(progressValue);
        if (elapsed >= autoplayDelay) {
          if (!isLoopEnabled && isAtLastSlide) {
            stopAtEnd = true;
            return;
          }
          embla?.scrollNext();
          lastStartTime = Date.now();
          progressValue = 0;
        }
        progressRAF = requestAnimationFrame(step);
      };
  
      progressRAF = requestAnimationFrame(step);
    }
    function updateProgressBarUI(value) {
      const emblaNode = wrapper.querySelector(".beae-slider-wrapper");
      const controls = wrapper.querySelector(".beae-slider-controls");
      if (!emblaNode || !controls) return;
      const autoplayEnabled = emblaNode.getAttribute("data-play") === "true";
      controls.style.setProperty("--beae-progress", autoplayEnabled ? `${value}` : "100");
    }
    function startAutoplay() {
      clearTimeout(autoplayTimeout);
      cancelAnimationFrame(progressRAF);
      lastStartTime = Date.now();
      progressValue = 0;
      stopAtEnd = false;
      animateProgressBar();
    }
    function pauseAutoplay() {
      clearTimeout(autoplayTimeout);
      cancelAnimationFrame(progressRAF);
      if (lastStartTime) {
        const elapsed = Date.now() - lastStartTime;
        remainingTime = Math.max(autoplayDelay - elapsed, 0);
        progressValue = (elapsed / autoplayDelay) * 100; 
      }
    }
    function resumeAutoplay() {
      clearTimeout(autoplayTimeout);
      cancelAnimationFrame(progressRAF);
      if (remainingTime != null) {
        lastStartTime = Date.now() - (autoplayDelay - remainingTime);
      } else {
        lastStartTime = Date.now();
      }
      animateProgressBar();
    }
    function setupAutoplay(node, autoplay, delay) {
      if (boundEmblaNode) {
        boundEmblaNode.removeEventListener("mouseenter", onMouseEnterPause);
        boundEmblaNode.removeEventListener("mouseleave", onMouseLeaveResume);
        boundEmblaNode = null;
      }
      pauseAutoplay();
      cancelAnimationFrame(progressRAF);
      autoplayDelay = delay;
      progressValue = 0;
      stopAtEnd = false;
      // Only allow autoplay if disabledInspector is true (inspector disabled = autoplay allowed)
      const autoplayAllowed = isAutoplayAllowed();
      if (autoplay && node && autoplayAllowed) {
        startAutoplay();
        node.addEventListener("mouseenter", onMouseEnterPause);
        node.addEventListener("mouseleave", onMouseLeaveResume);
        boundEmblaNode = node;
      }
    }
    function onMouseEnterPause() {
      isHovering = true;
      pauseAutoplay();
    }
    function onMouseLeaveResume() {
      isHovering = false;
      if (!stopAtEnd) {
        resumeAutoplay();
      }
    }
  
    // =====================
    // UI Helpers
    // =====================
    function updateSlideActive(itemsPerView, isFade) {
      if (!embla) return;
      const slides = embla.slideNodes();
      const totalSlides = slides.length;
      const selectedIndex = embla.selectedScrollSnap();
      const isLoop = embla.internalEngine().options.loop;
      slides.forEach((slide, index) => {
        slide.classList.remove("active", "active-prev", "active-next","active-main");
      });
      slides[selectedIndex]?.classList.add("active-main");
      if (isFade) {
        const currentSlide = slides[selectedIndex];
        if (currentSlide) {
          currentSlide.classList.add("active");
        }
        const prevIndex = (selectedIndex - 1 + totalSlides) % totalSlides;
        const nextIndex = (selectedIndex + 1) % totalSlides;
        slides[prevIndex]?.classList.add("active-prev");
        slides[nextIndex]?.classList.add("active-next");
      } else {
        const activeIndexes = Array.from({ length: itemsPerView }, (_, i) =>
          isLoop
            ? (selectedIndex + i) % totalSlides
            : selectedIndex + i
        );
        const prevIndex = isLoop
          ? (selectedIndex - 1 + totalSlides) % totalSlides
          : selectedIndex - 1;
        const nextIndex = isLoop
          ? (selectedIndex + itemsPerView) % totalSlides
          : selectedIndex + itemsPerView;
        slides.forEach((slide, index) => {
          if (activeIndexes.includes(index)) {
            slide.classList.add("active");
          } else if (index === prevIndex) {
            slide.classList.add("active-prev");
          } else if (index === nextIndex) {
            slide.classList.add("active-next");
          }
        });
      }
    }
  
    function createPagination(wrapper, emblaNode, paginationType, totalPages) {
      const paginationWrapper = wrapper.querySelector(".beae-slider-pagination-wrapper");
      if (!paginationWrapper) return { dotsArr: [], paginationWrapperInner: null };
      paginationWrapper.innerHTML = "";
      
      let paginationWrapperInner = null;
      let dotsArr = [];
      const enableSvg = paginationWrapper.dataset.paginationSvg === "true";
      const appendSvgToDot = (dot) => {
        if (!(paginationType === "dots" || paginationType === "numbers")) return;
        if (!enableSvg) return;
        if (dot.querySelector(".circular-progress")) return;
    
        const svgNS = "http://www.w3.org/2000/svg";
        const svg = document.createElementNS(svgNS, "svg");
        svg.setAttribute("class", "circular-progress");
        svg.setAttribute("aria-hidden", "true");
        svg.style.pointerEvents = "none";
    
        const bg = document.createElementNS(svgNS, "circle");
        bg.setAttribute("cx", "50");
        bg.setAttribute("cy", "50");
        bg.setAttribute("r", "45");
        bg.setAttribute("class", "circular-progress__bg");
    
        const fg = document.createElementNS(svgNS, "circle");
        fg.setAttribute("cx", "50");
        fg.setAttribute("cy", "50");
        fg.setAttribute("r", "45");
        fg.setAttribute("class", "circular-progress__fg");
    
        svg.appendChild(bg);
        svg.appendChild(fg);
        dot.appendChild(svg);
      };
      if (paginationType === "dynamic-dots") {
        const VIEW_WIDTH = getPaginationViewWidth(emblaNode);
        paginationWrapper.style.position = "relative";
        paginationWrapper.style.overflow = "hidden";
        paginationWrapper.style.width = `${VIEW_WIDTH}px`;
        paginationWrapperInner = document.createElement("div");
        paginationWrapperInner.classList.add( "pagination-bullets-dynamic");
        paginationWrapperInner.style.display = "flex";
        paginationWrapperInner.style.position = "relative";
        paginationWrapperInner.style.transition = "left 0.3s ease";
        paginationWrapper.appendChild(paginationWrapperInner);
        dotsArr = Array.from({ length: totalPages }, (_, i) => {
          const dot = document.createElement("span");
          dot.className = "beae-slider-pagination";
          dot.addEventListener("click", () => embla.scrollTo(i));
          paginationWrapperInner.appendChild(dot);
          return dot;
        });
      } else {
        paginationWrapper.style.width = "auto";
        dotsArr = Array.from({ length: totalPages }, (_, i) => {
          const dot = document.createElement("span");
          dot.className = "beae-slider-pagination";
          if (paginationType === "numbers") dot.textContent = i + 1;
          dot.addEventListener("click", () => embla.scrollTo(i));
          paginationWrapper.appendChild(dot);
          appendSvgToDot(dot);
          return dot;
        });
      }
      return { dotsArr, paginationWrapperInner };
    }
    function updateProgress(wrapper, selectedIndex, totalPages) {
      const progressWrapper = wrapper.querySelector(".beae-slider-pagination-progress");
      if (!progressWrapper) return;
      const progressEl = progressWrapper.querySelector("span");
      if (!progressEl) return;
      const type = progressWrapper.getAttribute("data-progress") || "grow";
      const stepPercent = 100 / totalPages;
      if (type === "slider") {
        progressEl.style.width = `${stepPercent}%`;
        progressEl.style.left = `${selectedIndex * stepPercent}%`;
      } else {
        const percent = ((selectedIndex + 1) / totalPages) * 100;
        progressEl.style.width = `${percent}%`;
        progressEl.style.left = "0";
      }
    }
    function bindDragLockWatcher(emblaNode, embla, options = {}, plugins = []) {
      const observer = new MutationObserver(() => {
        const locked = isDraggingActive(emblaNode);
        isLockedByChildDrag = locked;
    
        if (locked) {
          pauseAutoplay?.();
        } else {
          resumeAutoplay?.();
        }
  
        if (embla && typeof embla.reInit === 'function') {
          embla.reInit(options, plugins);
        }
      });
    
      observer.observe(emblaNode, {
        subtree: true,
        attributes: true,
        attributeFilter: ["data-beae-dragging"],
      });
    }
    // =====================
    // Main Init
    // =====================
    function initSlider() {
    
      const emblaNode = wrapper.querySelector(".beae-slider-wrapper");
      if (!emblaNode) return;
      const width = window.innerWidth;
      // Non-slider layout
      if (!isSliderLayout(width, emblaNode)) {
        embla?.destroy();
        embla = null;
        wrapper.querySelector(".beae-slider-pagination-wrapper")?.replaceChildren();
        const controls = wrapper.querySelector(".beae-slider-controls");
        controls?.removeAttribute("show-desktop");
        controls?.removeAttribute("show-tablet");
        controls?.removeAttribute("show-mobile");
        setupAutoplay(null, false, 3000);
        return;
      }
      // Config
      const slidesToScrollRaw = parseInt(emblaNode.getAttribute("data-scroll") || "1", 10);
      const alignValue = emblaNode.getAttribute("data-align") || "start";
      const paginationType = emblaNode.getAttribute("data-pagination") || "dots";
      const autoplay = emblaNode.getAttribute("data-play") === "true";
      const transition = parseTransition(emblaNode.getAttribute("data-transition"));
      const effect = emblaNode.getAttribute("data-effect") || "slide";
      const isFade = effect === "fade";
      let itemsPerView = getItemsPerView(width, emblaNode);
      const slidesToScroll = isFade ? 1 : Math.min(slidesToScrollRaw, itemsPerView);
      const items = emblaNode.querySelectorAll(".beae-slider-item");
      const totalItems = items.length;
      const itemLast = items[items.length - 1];
      let isLoop = emblaNode.getAttribute("data-loop") === "true";
      if (totalItems <= itemsPerView) {
        isLoop = false;
      }
      if (!isLoop) {
        itemLast.style.marginRight = "0px";
      } else {
        itemLast.style.marginRight = "";
      }
      emblaNode.setAttribute("data-init-loop", isLoop);
      
      // Determine axis
      const mediaProduct = wrapper.closest('.beae-media-product') || wrapper.querySelector('.beae-media-product');
      const thumbClasses = ['beae-product-media-thumbs', 'beae-product-thumb-slide'];
      const isThumb = thumbClasses.some(c => emblaNode.classList.contains(c));
      const pos = mediaProduct?.dataset?.positionThumbs;
      const isVerticalThumb = isThumb && width > 767.79 && (pos === 'left' || pos === 'right');
      const axis = isThumb ? (isVerticalThumb ? 'y' : 'x') : (emblaNode.getAttribute('data-axis') || 'x');
      emblaNode.setAttribute("data-axis", axis);
      const autoplayAllowed = isAutoplayAllowed();
      const options = {
          loop: isLoop,
          slidesToScroll,
          draggable: autoplayAllowed && !isFade,
          watchDrag: autoplayAllowed,
          dragFree: false,
          containScroll: isFade ? false : "trimSnaps",
          align: alignValue,
          axis: axis,
      };
      const plugins = isFade ? [EmblaCarouselFade()] : [];
     
      const itemsNode = emblaNode.querySelector('.beae-slider-items');
      if (itemsNode) {
        const isVertical = axis === 'y';
        Object.assign(itemsNode.style, {
          flexDirection: isVertical ? 'column' : '',
          marginLeft: isVertical ? '0' : '',
        });
        itemsNode.querySelectorAll('.beae-slider-item').forEach(item => {
          Object.assign(item.style, {
            paddingLeft: isVertical ? '0' : '',
            flex: isVertical ? '0 0 auto' : '',
            width: isVertical ? '100%' : '',
          });
        });
      }
      if (embla) {
        embla.reInit(options, plugins);
      } else {
        embla = EmblaCarousel(emblaNode, options, plugins);
        blockInteractionWhenDragging(emblaNode);
        bindDragLockWatcher(emblaNode, embla);
      }
      wrapper.embla = embla;

      // Direction attribute
      let lastIndex = embla.selectedScrollSnap();
      embla.on("select", () => {
        if (isLockedByChildDrag) {
          embla.scrollTo(embla.selectedScrollSnap(), true);
          return;
        }

        const itemsNode = emblaNode.querySelector('.beae-slider-items');
        const selectedIndex = embla.selectedScrollSnap();
        const totalSlides = embla.slideNodes().length;
        const width = Math.floor(window.innerWidth);
        const itemsPerView = getItemsPerView(width, emblaNode);

        if (!itemsNode) return;
        const autoplayAllowed = isAutoplayAllowed();
        if (autoplayAllowed) return;
        itemsNode.style.marginTop = '1px';
        const handleSettle = () => {
          itemsNode.style.marginTop = '';
          
          if (itemsPerView === 1) {
            const isMainMedia = emblaNode.closest('.beae-product-media-main');
        
            if (!isMainMedia) {
                const rect = emblaNode.getBoundingClientRect();
                const offset = 10; 
                const centerX = rect.right - offset;
                const centerY = rect.top + offset;
                let target = document.elementFromPoint(centerX, centerY);
        
                if (target) {
                    const slideItem = target.closest('.beae-slider-item') || target; 
                    const options = {
                        view: window,
                        bubbles: true,
                        cancelable: true,
                        clientX: centerX,
                        clientY: centerY
                    };
        
                    slideItem.dispatchEvent(new MouseEvent('mousedown', options));
                    slideItem.dispatchEvent(new MouseEvent('mouseup', options));
                    slideItem.dispatchEvent(new MouseEvent('click', options));
                }
            } 
        }
          embla.off("settle", handleSettle);
        };
        embla.on("settle", handleSettle);

       
        emblaNode.setAttribute("data-dir", selectedIndex > lastIndex ? "left" : "right");
        if (stopAtEnd && selectedIndex < totalSlides - itemsPerView) {
          stopAtEnd = false;
          resumeAutoplay();
          animateProgressBar();
        }
        remainingTime = autoplayDelay;
        lastStartTime = Date.now();
        lastIndex = selectedIndex;
        resetProgress();
      });
      // Pagination
      if (isFade) itemsPerView = 1;
      const getTotalPages = () => embla.scrollSnapList().length;
      let { dotsArr, paginationWrapperInner } = createPagination(wrapper, emblaNode, paginationType, getTotalPages());
      dots = dotsArr;
      // Update UI
      function updateUI() {
        const selectedIndex = embla.selectedScrollSnap();
        const totalPages = getTotalPages();
        const dots = wrapper.querySelectorAll(".beae-slider-pagination");
        const paginationTotalEl = wrapper.querySelector(".beae-slider-pagination-total span");
        if (paginationTotalEl) paginationTotalEl.textContent = `${selectedIndex + 1}/${totalPages}`;
        dots.forEach((dot, i) => {
          dot.className = "beae-slider-pagination";
          if (i === selectedIndex) dot.classList.add("active");
          else if (i === selectedIndex - 1) dot.classList.add("prev");
          else if (i === selectedIndex - 2) dot.classList.add("prev-prev");
          else if (i === selectedIndex + 1) dot.classList.add("next");
          else if (i === selectedIndex + 2) dot.classList.add("next-next");
        });
        updateProgress(wrapper, selectedIndex, totalPages);
        // Dynamic dots move
        if (paginationType === "dynamic-dots" && paginationWrapperInner) {
          const realDots = wrapper.querySelectorAll(".beae-slider-pagination");
          if (!realDots) return;
          const style = getComputedStyle(realDots[0]);
          const DOT_SIZE = parseFloat(style.width) || 0;
          const DOT_MARGIN = parseFloat(style.marginLeft) || 0;
          const DOT_FULL = DOT_SIZE + DOT_MARGIN * 2;
          const baseOffset = DOT_FULL * 2;
          const offset = baseOffset - selectedIndex * DOT_FULL;
          realDots.forEach(dot => {
            dot.style.left = `${offset}px`;
          });
        }
        // Controls visibility
        const controls = wrapper.querySelector(".beae-slider-controls");
        setTimeout(() => {
          if (controls) {
            controls.removeAttribute("show-desktop");
            controls.removeAttribute("show-tablet");
            controls.removeAttribute("show-mobile");
            if (width >= 1180 && totalItems > getItemsPerView(1180, emblaNode)) {
              controls.setAttribute("show-desktop", "true");
            } else if (width >= 767 && width <= 1180 && totalItems > getItemsPerView(767.79, emblaNode)) {
              controls.setAttribute("show-tablet", "true");
            } else if (width <=  767 && totalItems > getItemsPerView(375, emblaNode)) {
              controls.setAttribute("show-mobile", "true");
            }
            if (controls) {
              controls.style.setProperty("--height-controls", `${controls.offsetHeight}px`);
            }
          }
        }, 100);
        const btnPrev = wrapper.querySelector(".beae-slider-nav-left");
        const btnNext = wrapper.querySelector(".beae-slider-nav-right");
        if (btnPrev) {
          btnPrev.disabled = !embla.canScrollPrev();
          btnPrev.classList.toggle("disabled", !embla.canScrollPrev());
        }
        if (btnNext) {
          btnNext.disabled = !embla.canScrollNext();
          btnNext.classList.toggle("disabled", !embla.canScrollNext());
        }
        updateSlideActive(itemsPerView, isFade);
      }
      const btnPrev = wrapper.querySelector(".beae-slider-nav-left") 
      const btnNext = wrapper.querySelector(".beae-slider-nav-right") 
      if (btnPrev && !btnPrev.dataset.hasListener) {
        btnPrev.addEventListener("click", () => embla?.scrollPrev());
        btnPrev.dataset.hasListener = "true";
      }
      if (btnNext && !btnNext.dataset.hasListener) {
        btnNext.addEventListener("click", () => embla?.scrollNext());
        btnNext.dataset.hasListener = "true";
      }
      embla.off("select", updateUI);
      embla.on("select", updateUI);
      embla.on("reInit", () => {
        blockInteractionWhenDragging(emblaNode);
        setupAutoplay(
          embla.containerNode(),
          embla.containerNode().getAttribute("data-play") === "true",
          parseTransition(embla.containerNode().getAttribute("data-transition"))
        );
        resetProgress();
        dots.forEach(dot => dot.removeEventListener("click", () => { }));
        dots = [];
        const result = createPagination(wrapper, emblaNode, paginationType, getTotalPages());
        dots = result.dotsArr;
        paginationWrapperInner = result.paginationWrapperInner;
        updateUI();
      });
      
      // Listen to inspector state changes from parent window
      function handleInspectorStateChange() {
        const shouldAutoplay = emblaNode.getAttribute("data-play") === "true";
        const autoplayAllowed = isAutoplayAllowed();
      
        const newOptions = {
          ...options,
          draggable: autoplayAllowed,
          watchDrag: autoplayAllowed,
        };
      
        embla.reInit(newOptions, plugins);
      
        setupAutoplay(
          emblaNode,
          shouldAutoplay && autoplayAllowed,
          parseTransition(emblaNode.getAttribute("data-transition"))
        );
      }
      
      // Add event listener for inspector state changes
      window.addEventListener("beae-inspector-state-change", handleInspectorStateChange);
      
      setupAutoplay(emblaNode, autoplay, transition);
      resetProgress();
      updateUI();
      if (wrapper.emblaThumb) {
        wrapper.emblaThumb.destroy();
        wrapper.emblaThumb = null;
      }
      // Thumbnails
      function initEmblaThumbs(wrapper) {
        const mediaProduct = wrapper.querySelector('.beae-media-product');
        const emblaThumbNode = wrapper.querySelector('.beae-product-thumb-slide');
        if (!emblaThumbNode) return;
        const viewportWidth = window.innerWidth;
        let isVertical = false;

        if (viewportWidth > 1180) {
          isVertical = mediaProduct?.dataset.positionThumbs !== 'bottom';
        } else if (viewportWidth > 767) {
          isVertical = mediaProduct?.dataset.positionThumbsTablet !== 'bottom';
        }

        const alignValue = emblaNode.getAttribute("data-align") || "start";

        const OPTIONS_THUMBS = {
          dragFree: true,
          containScroll: 'keepSnaps',
          selectedClass: 'is-active',
          align: alignValue,
          axis: isVertical ? 'y' : 'x',
        };
        if (wrapper.emblaThumb) {
          wrapper.emblaThumb.destroy();
          wrapper.emblaThumb = null;
        }
        const emblaThumb = EmblaCarousel(emblaThumbNode, OPTIONS_THUMBS);
        wrapper.emblaThumb = emblaThumb;
        const syncThumbs = () => {
          const index = wrapper.embla.selectedScrollSnap();
          emblaThumb.slideNodes().forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === index);
          });
          emblaThumb.scrollTo(index);
        };
        wrapper.embla.on('select', syncThumbs);
        syncThumbs();
        emblaThumb.slideNodes().forEach((thumb, index) => {
          thumb.addEventListener('click', () => wrapper.embla.scrollTo(index));
        });
      }
      // --- Init
      initEmblaThumbs(wrapper);
      // --- Resize observer
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => initEmblaThumbs(wrapper), 300);
      });
      // Editor support
      function attachActivatedObserver(embla) {
        const items = embla.slideNodes();
        if (!items.length) return;
        const activatedObserver = new MutationObserver(mutationsList => {
          for (const mutation of mutationsList) {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "data-beae-block-activated"
            ) {
              const activatedItem = mutation.target;
              if (activatedItem.getAttribute("data-beae-block-activated") === "true") {
                const index = Array.from(items).indexOf(activatedItem);
                if (index !== -1) {
                  embla.scrollTo(index, true)
                }
              }
            }
          }
        });
        items.forEach(item => {
          activatedObserver.observe(item, {
            attributes: true,
            attributeFilter: ["data-beae-block-activated"],
          });
        });
      }
      function observeItemReorder(embla, wrapper) {
        let debounceTimer = null;
        const reorderObserver = new MutationObserver(() => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            attachActivatedObserver(embla);
          }, 1000);
        });
        reorderObserver.observe(wrapper, {
          childList: true,
          subtree: true,
        });
      }
      attachActivatedObserver(embla);
      observeItemReorder(embla, embla.containerNode());
    }
  
    // =====================
    // Resize debounce
    // =====================
    let resizeTimeout;
    let lastWidth = window.innerWidth;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const currentWidth = window.innerWidth;
        if (Math.abs(currentWidth - lastWidth) >= 100) {
          let hasActivated = emblaNodeObserver.querySelector('[data-beae-block-activated]');
          if (hasActivated) {
            setTimeout(() => {
              let activeItem = emblaNodeObserver.querySelector(".beae-slider-item.active");
              if (activeItem) {
                activeItem.click();
              }
            }, 50);
          }
          initSlider();
          lastWidth = currentWidth;
        }
      }, 100);
    });
  
    // =====================
    // Mutation observer for attrs
    // =====================
    const emblaNodeObserver = wrapper.querySelector(".beae-slider-wrapper");
    
    if (emblaNodeObserver) {
      const observer = new MutationObserver((mutations) => {
        if (
          mutations.some((m) =>
            [
              "data-loop", "data-effect", "data-align", "data-scroll",
              "data-pagination-width", "data-pagination", "data-navigation",
              "data-item", "data-item-table", "data-item-mobile",
              "data-play", "data-transition",
              "data-desktop-layout", "data-tablet-layout", "data-mobile-layout",
              "data-position-thumbs",  "data-position-thumbs-tablet"
            ].includes(m.attributeName)
          )
        ) {
          initSlider();
        }
      });
      observer.observe(emblaNodeObserver, { attributes: true });
    }
    const mediaProductEl = wrapper.closest('.beae-media-product') || wrapper.querySelector('.beae-media-product');
    if (mediaProductEl) {
      const parentObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'data-position-thumbs' || mutation.attributeName === 'data-position-thumbs-tablet') {
            initSlider();
          }
        }
      });
      parentObserver.observe(mediaProductEl, {
        attributes: true,
        attributeFilter: ['data-position-thumbs','data-position-thumbs-tablet'],
      });
    }

    initSlider();
  };
}


!function (n, t) { "object" == typeof exports && "undefined" != typeof module ? module.exports = t() : "function" == typeof define && define.amd ? define(t) : (n = "undefined" != typeof globalThis ? globalThis : n || self).EmblaCarousel = t() }(this, (function () { "use strict"; function n(n) { return "number" == typeof n } function t(n) { return "string" == typeof n } function e(n) { return "boolean" == typeof n } function r(n) { return "[object Object]" === Object.prototype.toString.call(n) } function o(n) { return Math.abs(n) } function i(n) { return Math.sign(n) } function c(n, t) { return o(n - t) } function u(n) { return f(n).map(Number) } function s(n) { return n[a(n)] } function a(n) { return Math.max(0, n.length - 1) } function d(n, t) { return t === a(n) } function l(n, t = 0) { return Array.from(Array(n), ((n, e) => t + e)) } function f(n) { return Object.keys(n) } function p(n, t) { return [n, t].reduce(((n, t) => (f(t).forEach((e => { const o = n[e], i = t[e], c = r(o) && r(i); n[e] = c ? p(o, i) : i })), n)), {}) } function m(n, t) { return void 0 !== t.MouseEvent && n instanceof t.MouseEvent } function g() { let n = []; const t = { add: function (e, r, o, i = { passive: !0 }) { let c; if ("addEventListener" in e) e.addEventListener(r, o, i), c = () => e.removeEventListener(r, o, i); else { const n = e; n.addListener(o), c = () => n.removeListener(o) } return n.push(c), t }, clear: function () { n = n.filter((n => n())) } }; return t } function h(n, t, e, r) { const o = g(), i = 1e3 / 60; let c = null, u = 0, s = 0; function a(n) { if (!s) return; c || (c = n, e(), e()); const o = n - c; for (c = n, u += o; u >= i;)e(), u -= i; r(u / i), s && (s = t.requestAnimationFrame(a)) } function d() { t.cancelAnimationFrame(s), c = null, u = 0, s = 0 } return { init: function () { o.add(n, "visibilitychange", (() => { n.hidden && (c = null, u = 0) })) }, destroy: function () { d(), o.clear() }, start: function () { s || (s = t.requestAnimationFrame(a)) }, stop: d, update: e, render: r } } function x(n = 0, t = 0) { const e = o(n - t); function r(t) { return t < n } function i(n) { return n > t } function c(n) { return r(n) || i(n) } return { length: e, max: t, min: n, constrain: function (e) { return c(e) ? r(e) ? n : t : e }, reachedAny: c, reachedMax: i, reachedMin: r, removeOffset: function (n) { return e ? n - e * Math.ceil((n - t) / e) : n } } } function y(n, t, e) { const { constrain: r } = x(0, n), i = n + 1; let c = u(t); function u(n) { return e ? o((i + n) % i) : r(n) } function s() { return c } function a() { return y(n, s(), e) } const d = { get: s, set: function (n) { return c = u(n), d }, add: function (n) { return a().set(s() + n) }, clone: a }; return d } function v(n, t, r, u, s, a, d, l, f, p, h, y, v, b, S, w, E, L, D) { const { cross: I, direction: M } = n, A = ["INPUT", "SELECT", "TEXTAREA"], F = { passive: !1 }, T = g(), O = g(), P = x(50, 225).constrain(b.measure(20)), z = { mouse: 300, touch: 400 }, H = { mouse: 500, touch: 600 }, k = S ? 43 : 25; let V = !1, B = 0, C = 0, N = !1, R = !1, j = !1, G = !1; function q(n) { if (!m(n, u) && n.touches.length >= 2) return U(n); const t = a.readPoint(n), e = a.readPoint(n, I), r = c(t, B), o = c(e, C); if (!R && !G) { if (!n.cancelable) return U(n); if (R = r > o, !R) return U(n) } const i = a.pointerMove(n); r > w && (j = !0), p.useFriction(.3).useDuration(.75), l.start(), s.add(M(i)), n.preventDefault() } function U(n) { const t = h.byDistance(0, !1).index !== y.get(), e = a.pointerUp(n) * (S ? H : z)[G ? "mouse" : "touch"], r = function (n, t) { const e = y.add(-1 * i(n)), r = h.byDistance(n, !S).distance; return S || o(n) < P ? r : E && t ? .5 * r : h.byIndex(e.get(), 0).distance }(M(e), t), u = function (n, t) { if (0 === n || 0 === t) return 0; if (o(n) <= o(t)) return 0; const e = c(o(n), o(t)); return o(e / n) }(e, r), s = k - 10 * u, d = L + u / 50; R = !1, N = !1, O.clear(), p.useDuration(s).useFriction(d), f.distance(r, !S), G = !1, v.emit("pointerUp") } function W(n) { j && (n.stopPropagation(), n.preventDefault(), j = !1) } return { init: function (n) { if (!D) return; function o(o) { (e(D) || D(n, o)) && function (n) { const e = m(n, u); if (G = e, j = S && e && !n.buttons && V, V = c(s.get(), d.get()) >= 2, e && 0 !== n.button) return; if (function (n) { const t = n.nodeName || ""; return A.includes(t) }(n.target)) return; N = !0, a.pointerDown(n), p.useFriction(0).useDuration(0), s.set(d), function () { const n = G ? r : t; O.add(n, "touchmove", q, F).add(n, "touchend", U).add(n, "mousemove", q, F).add(n, "mouseup", U) }(), B = a.readPoint(n), C = a.readPoint(n, I), v.emit("pointerDown") }(o) } const i = t; T.add(i, "dragstart", (n => n.preventDefault()), F).add(i, "touchmove", (() => { }), F).add(i, "touchend", (() => { })).add(i, "touchstart", o).add(i, "mousedown", o).add(i, "touchcancel", U).add(i, "contextmenu", U).add(i, "click", W, !0) }, destroy: function () { T.clear(), O.clear() }, pointerDown: function () { return N } } } function b(n, t) { let e, r; function i(n) { return n.timeStamp } function c(e, r) { const o = "client" + ("x" === (r || n.scroll) ? "X" : "Y"); return (m(e, t) ? e : e.touches[0])[o] } return { pointerDown: function (n) { return e = n, r = n, c(n) }, pointerMove: function (n) { const t = c(n) - c(r), o = i(n) - i(e) > 170; return r = n, o && (e = n), t }, pointerUp: function (n) { if (!e || !r) return 0; const t = c(r) - c(e), u = i(n) - i(e), s = i(n) - i(r) > 170, a = t / u; return u && !s && o(a) > .1 ? a : 0 }, readPoint: c } } function S(n, t, r, i, c, u, s) { const a = [n].concat(i); let d, l, f = [], p = !1; function m(n) { return c.measureSize(s.measure(n)) } return { init: function (c) { u && (l = m(n), f = i.map(m), d = new ResizeObserver((r => { (e(u) || u(c, r)) && function (e) { for (const r of e) { if (p) return; const e = r.target === n, u = i.indexOf(r.target), s = e ? l : f[u]; if (o(m(e ? n : i[u]) - s) >= .5) { c.reInit(), t.emit("resize"); break } } }(r) })), r.requestAnimationFrame((() => { a.forEach((n => d.observe(n))) }))) }, destroy: function () { p = !0, d && d.disconnect() } } } function w(n, t, e, r, i) { const c = i.measure(10), u = i.measure(50), s = x(.1, .99); let a = !1; function d() { return !a && (!!n.reachedAny(e.get()) && !!n.reachedAny(t.get())) } return { shouldConstrain: d, constrain: function (i) { if (!d()) return; const a = n.reachedMin(t.get()) ? "min" : "max", l = o(n[a] - t.get()), f = e.get() - t.get(), p = s.constrain(l / u); e.subtract(f * p), !i && o(f) < c && (e.set(n.constrain(e.get())), r.useDuration(25).useBaseFriction()) }, toggleActive: function (n) { a = !n } } } function E(n, t, e, r) { const o = t.min + .1, i = t.max + .1, { reachedMin: c, reachedMax: u } = x(o, i); return { loop: function (t) { if (!function (n) { return 1 === n ? u(e.get()) : -1 === n && c(e.get()) }(t)) return; const o = n * (-1 * t); r.forEach((n => n.add(o))) } } } function L(n, t, e, r, c) { const { reachedAny: u, removeOffset: a, constrain: d } = r; function l(n) { return n.concat().sort(((n, t) => o(n) - o(t)))[0] } function f(t, r) { const o = [t, t + e, t - e]; if (!n) return t; if (!r) return l(o); const c = o.filter((n => i(n) === r)); return c.length ? l(c) : s(o) - e } return { byDistance: function (e, r) { const i = c.get() + e, { index: s, distance: l } = function (e) { const r = n ? a(e) : d(e), i = t.map(((n, t) => ({ diff: f(n - r, 0), index: t }))).sort(((n, t) => o(n.diff) - o(t.diff))), { index: c } = i[0]; return { index: c, distance: r } }(i), p = !n && u(i); return !r || p ? { index: s, distance: e } : { index: s, distance: e + f(t[s] - l, 0) } }, byIndex: function (n, e) { return { index: n, distance: f(t[n] - c.get(), e) } }, shortcut: f } } function D(t, r, o, i, c, u, s, a) { const d = { passive: !0, capture: !0 }; let l = 0; function f(n) { "Tab" === n.code && (l = (new Date).getTime()) } return { init: function (p) { a && (u.add(document, "keydown", f, !1), r.forEach(((r, f) => { u.add(r, "focus", (r => { (e(a) || a(p, r)) && function (e) { if ((new Date).getTime() - l > 10) return; s.emit("slideFocusStart"), t.scrollLeft = 0; const r = o.findIndex((n => n.includes(e))); n(r) && (c.useDuration(0), i.index(r, 0), s.emit("slideFocus")) }(f) }), d) }))) } } } function I(t) { let e = t; function r(t) { return n(t) ? t : t.get() } return { get: function () { return e }, set: function (n) { e = r(n) }, add: function (n) { e += r(n) }, subtract: function (n) { e -= r(n) } } } function M(n, t) { const e = "x" === n.scroll ? function (n) { return `translate3d(${n}px,0px,0px)` } : function (n) { return `translate3d(0px,${n}px,0px)` }, r = t.style; let o = null, i = !1; return { clear: function () { i || (r.transform = "", t.getAttribute("style") || t.removeAttribute("style")) }, to: function (t) { if (i) return; const c = (u = n.direction(t), Math.round(100 * u) / 100); var u; c !== o && (r.transform = e(c), o = c) }, toggleActive: function (n) { i = !n } } } function A(n, t, e, r, o, i, c, s, a) { const d = .5, l = u(o), f = u(o).reverse(), p = function () { const n = c[0]; return h(g(f, n), e, !1) }().concat(function () { const n = t - c[0] - 1; return h(g(l, n), -e, !0) }()); function m(n, t) { return n.reduce(((n, t) => n - o[t]), t) } function g(n, t) { return n.reduce(((n, e) => m(n, t) > 0 ? n.concat([e]) : n), []) } function h(o, c, u) { const l = function (n) { return i.map(((e, o) => ({ start: e - r[o] + d + n, end: e + t - d + n }))) }(c); return o.map((t => { const r = u ? 0 : -e, o = u ? e : 0, i = u ? "end" : "start", c = l[t][i]; return { index: t, loopPoint: c, slideLocation: I(-1), translate: M(n, a[t]), target: () => s.get() > c ? r : o } })) } return { canLoop: function () { return p.every((({ index: n }) => m(l.filter((t => t !== n)), t) <= .1)) }, clear: function () { p.forEach((n => n.translate.clear())) }, loop: function () { p.forEach((n => { const { target: t, translate: e, slideLocation: r } = n, o = t(); o !== r.get() && (e.to(o), r.set(o)) })) }, loopPoints: p } } function F(n, t, r) { let o, i = !1; return { init: function (c) { r && (o = new MutationObserver((n => { i || (e(r) || r(c, n)) && function (n) { for (const e of n) if ("childList" === e.type) { c.reInit(), t.emit("slidesChanged"); break } }(n) })), o.observe(n, { childList: !0 })) }, destroy: function () { o && o.disconnect(), i = !0 } } } function T(n, t, e, r) { const o = {}; let i, c = null, u = null, s = !1; return { init: function () { i = new IntersectionObserver((n => { s || (n.forEach((n => { const e = t.indexOf(n.target); o[e] = n })), c = null, u = null, e.emit("slidesInView")) }), { root: n.parentElement, threshold: r }), t.forEach((n => i.observe(n))) }, destroy: function () { i && i.disconnect(), s = !0 }, get: function (n = !0) { if (n && c) return c; if (!n && u) return u; const t = function (n) { return f(o).reduce(((t, e) => { const r = parseInt(e), { isIntersecting: i } = o[r]; return (n && i || !n && !i) && t.push(r), t }), []) }(n); return n && (c = t), n || (u = t), t } } } function O(t, e, r, i, c, d, l, f, p) { const { startEdge: m, endEdge: g, direction: h } = t, x = n(r); return { groupSlides: function (n) { return x ? function (n, t) { return u(n).filter((n => n % t == 0)).map((e => n.slice(e, e + t))) }(n, r) : function (n) { return n.length ? u(n).reduce(((t, r, u) => { const x = s(t) || 0, y = 0 === x, v = r === a(n), b = c[m] - d[x][m], S = c[m] - d[r][g], w = !i && y ? h(l) : 0, E = o(S - (!i && v ? h(f) : 0) - (b + w)); return u && E > e + p && t.push(r), v && t.push(n.length), t }), []).map(((t, e, r) => { const o = Math.max(r[e - 1] || 0); return n.slice(o, t) })) : [] }(n) } } } function P(n, e, r, f, p, m, P) { const { align: z, axis: H, direction: k, startIndex: V, loop: B, duration: C, dragFree: N, dragThreshold: R, inViewThreshold: j, slidesToScroll: G, skipSnaps: q, containScroll: U, watchResize: W, watchSlides: $, watchDrag: Q, watchFocus: X } = m, Y = { measure: function (n) { const { offsetTop: t, offsetLeft: e, offsetWidth: r, offsetHeight: o } = n; return { top: t, right: e + r, bottom: t + o, left: e, width: r, height: o } } }, J = Y.measure(e), K = r.map(Y.measure), Z = function (n, t) { const e = "rtl" === t, r = "y" === n, o = !r && e ? -1 : 1; return { scroll: r ? "y" : "x", cross: r ? "x" : "y", startEdge: r ? "top" : e ? "right" : "left", endEdge: r ? "bottom" : e ? "left" : "right", measureSize: function (n) { const { height: t, width: e } = n; return r ? t : e }, direction: function (n) { return n * o } } }(H, k), _ = Z.measureSize(J), nn = function (n) { return { measure: function (t) { return n * (t / 100) } } }(_), tn = function (n, e) { const r = { start: function () { return 0 }, center: function (n) { return o(n) / 2 }, end: o }; function o(n) { return e - n } return { measure: function (o, i) { return t(n) ? r[n](o) : n(e, o, i) } } }(z, _), en = !B && !!U, rn = B || !!U, { slideSizes: on, slideSizesWithGaps: cn, startGap: un, endGap: sn } = function (n, t, e, r, i, c) { const { measureSize: u, startEdge: a, endEdge: l } = n, f = e[0] && i, p = function () { if (!f) return 0; const n = e[0]; return o(t[a] - n[a]) }(), m = function () { if (!f) return 0; const n = c.getComputedStyle(s(r)); return parseFloat(n.getPropertyValue(`margin-${l}`)) }(), g = e.map(u), h = e.map(((n, t, e) => { const r = !t, o = d(e, t); return r ? g[t] + p : o ? g[t] + m : e[t + 1][a] - n[a] })).map(o); return { slideSizes: g, slideSizesWithGaps: h, startGap: p, endGap: m } }(Z, J, K, r, rn, p), an = O(Z, _, G, B, J, K, un, sn, 2), { snaps: dn, snapsAligned: ln } = function (n, t, e, r, i) { const { startEdge: c, endEdge: u } = n, { groupSlides: a } = i, d = a(r).map((n => s(n)[u] - n[0][c])).map(o).map(t.measure), l = r.map((n => e[c] - n[c])).map((n => -o(n))), f = a(l).map((n => n[0])).map(((n, t) => n + d[t])); return { snaps: l, snapsAligned: f } }(Z, tn, J, K, an), fn = -s(dn) + s(cn), { snapsContained: pn, scrollContainLimit: mn } = function (n, t, e, r, o) { const i = x(-t + n, 0), u = e.map(((n, t) => { const { min: r, max: o } = i, c = i.constrain(n), u = !t, s = d(e, t); return u ? o : s || l(r, c) ? r : l(o, c) ? o : c })).map((n => parseFloat(n.toFixed(3)))), a = function () { const n = u[0], t = s(u); return x(u.lastIndexOf(n), u.indexOf(t) + 1) }(); function l(n, t) { return c(n, t) <= 1 } return { snapsContained: function () { if (t <= n + o) return [i.max]; if ("keepSnaps" === r) return u; const { min: e, max: c } = a; return u.slice(e, c) }(), scrollContainLimit: a } }(_, fn, ln, U, 2), gn = en ? pn : ln, { limit: hn } = function (n, t, e) { const r = t[0]; return { limit: x(e ? r - n : s(t), r) } }(fn, gn, B), xn = y(a(gn), V, B), yn = xn.clone(), vn = u(r), bn = h(f, p, (() => (({ dragHandler: n, scrollBody: t, scrollBounds: e, options: { loop: r } }) => { r || e.constrain(n.pointerDown()), t.seek() })(Hn)), (n => (({ scrollBody: n, translate: t, location: e, offsetLocation: r, previousLocation: o, scrollLooper: i, slideLooper: c, dragHandler: u, animation: s, eventHandler: a, scrollBounds: d, options: { loop: l } }, f) => { const p = n.settled(), m = !d.shouldConstrain(), g = l ? p : p && m, h = g && !u.pointerDown(); h && s.stop(); const x = e.get() * f + o.get() * (1 - f); r.set(x), l && (i.loop(n.direction()), c.loop()), t.to(r.get()), h && a.emit("settle"), g || a.emit("scroll") })(Hn, n))), Sn = gn[xn.get()], wn = I(Sn), En = I(Sn), Ln = I(Sn), Dn = I(Sn), In = function (n, t, e, r, c, u) { let s = 0, a = 0, d = c, l = u, f = n.get(), p = 0; function m(n) { return d = n, h } function g(n) { return l = n, h } const h = { direction: function () { return a }, duration: function () { return d }, velocity: function () { return s }, seek: function () { const t = r.get() - n.get(); let o = 0; return d ? (e.set(n), s += t / d, s *= l, f += s, n.add(s), o = f - p) : (s = 0, e.set(r), n.set(r), o = t), a = i(o), p = f, h }, settled: function () { return o(r.get() - t.get()) < .001 }, useBaseFriction: function () { return g(u) }, useBaseDuration: function () { return m(c) }, useFriction: g, useDuration: m }; return h }(wn, Ln, En, Dn, C, .68), Mn = L(B, gn, fn, hn, Dn), An = function (n, t, e, r, o, i, c) { function u(o) { const u = o.distance, s = o.index !== t.get(); i.add(u), u && (r.duration() ? n.start() : (n.update(), n.render(1), n.update())), s && (e.set(t.get()), t.set(o.index), c.emit("select")) } return { distance: function (n, t) { u(o.byDistance(n, t)) }, index: function (n, e) { const r = t.clone().set(n); u(o.byIndex(r.get(), e)) } } }(bn, xn, yn, In, Mn, Dn, P), Fn = function (n) { const { max: t, length: e } = n; return { get: function (n) { return e ? (n - t) / -e : 0 } } }(hn), Tn = g(), On = T(e, r, P, j), { slideRegistry: Pn } = function (n, t, e, r, o, i) { const { groupSlides: c } = o, { min: u, max: f } = r; return { slideRegistry: function () { const r = c(i), o = !n || "keepSnaps" === t; return 1 === e.length ? [i] : o ? r : r.slice(u, f).map(((n, t, e) => { const r = !t, o = d(e, t); return r ? l(s(e[0]) + 1) : o ? l(a(i) - s(e)[0] + 1, s(e)[0]) : n })) }() } }(en, U, gn, mn, an, vn), zn = D(n, r, Pn, An, In, Tn, P, X), Hn = { ownerDocument: f, ownerWindow: p, eventHandler: P, containerRect: J, slideRects: K, animation: bn, axis: Z, dragHandler: v(Z, n, f, p, Dn, b(Z, p), wn, bn, An, In, Mn, xn, P, nn, N, R, q, .68, Q), eventStore: Tn, percentOfView: nn, index: xn, indexPrevious: yn, limit: hn, location: wn, offsetLocation: Ln, previousLocation: En, options: m, resizeHandler: S(e, P, p, r, Z, W, Y), scrollBody: In, scrollBounds: w(hn, Ln, Dn, In, nn), scrollLooper: E(fn, hn, Ln, [wn, Ln, En, Dn]), scrollProgress: Fn, scrollSnapList: gn.map(Fn.get), scrollSnaps: gn, scrollTarget: Mn, scrollTo: An, slideLooper: A(Z, _, fn, on, cn, dn, gn, Ln, r), slideFocus: zn, slidesHandler: F(e, P, $), slidesInView: On, slideIndexes: vn, slideRegistry: Pn, slidesToScroll: an, target: Dn, translate: M(Z, e) }; return Hn } const z = { align: "center", axis: "x", container: null, slides: null, containScroll: "trimSnaps", direction: "ltr", slidesToScroll: 1, inViewThreshold: 0, breakpoints: {}, dragFree: !1, dragThreshold: 10, loop: !1, skipSnaps: !1, duration: 25, startIndex: 0, active: !0, watchDrag: !0, watchResize: !0, watchSlides: !0, watchFocus: !0 }; function H(n) { function t(n, t) { return p(n, t || {}) } const e = { mergeOptions: t, optionsAtMedia: function (e) { const r = e.breakpoints || {}, o = f(r).filter((t => n.matchMedia(t).matches)).map((n => r[n])).reduce(((n, e) => t(n, e)), {}); return t(e, o) }, optionsMediaQueries: function (t) { return t.map((n => f(n.breakpoints || {}))).reduce(((n, t) => n.concat(t)), []).map(n.matchMedia) } }; return e } function k(n, e, r) { const o = n.ownerDocument, i = o.defaultView, c = H(i), u = function (n) { let t = []; return { init: function (e, r) { return t = r.filter((({ options: t }) => !1 !== n.optionsAtMedia(t).active)), t.forEach((t => t.init(e, n))), r.reduce(((n, t) => Object.assign(n, { [t.name]: t })), {}) }, destroy: function () { t = t.filter((n => n.destroy())) } } }(c), s = g(), a = function () { let n, t = {}; function e(n) { return t[n] || [] } const r = { init: function (t) { n = t }, emit: function (t) { return e(t).forEach((e => e(n, t))), r }, off: function (n, o) { return t[n] = e(n).filter((n => n !== o)), r }, on: function (n, o) { return t[n] = e(n).concat([o]), r }, clear: function () { t = {} } }; return r }(), { mergeOptions: d, optionsAtMedia: l, optionsMediaQueries: f } = c, { on: p, off: m, emit: h } = a, x = A; let y, v, b, S, w = !1, E = d(z, k.globalOptions), L = d(E), D = []; function I(t) { const e = P(n, b, S, o, i, t, a); if (t.loop && !e.slideLooper.canLoop()) { return I(Object.assign({}, t, { loop: !1 })) } return e } function M(e, r) { w || (E = d(E, e), L = l(E), D = r || D, function () { const { container: e, slides: r } = L, o = t(e) ? n.querySelector(e) : e; b = o || n.children[0]; const i = t(r) ? b.querySelectorAll(r) : r; S = [].slice.call(i || b.children) }(), y = I(L), f([E, ...D.map((({ options: n }) => n))]).forEach((n => s.add(n, "change", A))), L.active && (y.translate.to(y.location.get()), y.animation.init(), y.slidesInView.init(), y.slideFocus.init(V), y.eventHandler.init(V), y.resizeHandler.init(V), y.slidesHandler.init(V), y.options.loop && y.slideLooper.loop(), b.offsetParent && S.length && y.dragHandler.init(V), v = u.init(V, D))) } function A(n, t) { const e = O(); F(), M(d({ startIndex: e }, n), t), a.emit("reInit") } function F() { y.dragHandler.destroy(), y.eventStore.clear(), y.translate.clear(), y.slideLooper.clear(), y.resizeHandler.destroy(), y.slidesHandler.destroy(), y.slidesInView.destroy(), y.animation.destroy(), u.destroy(), s.clear() } function T(n, t, e) { L.active && !w && (y.scrollBody.useBaseFriction().useDuration(!0 === t ? 0 : L.duration), y.scrollTo.index(n, e || 0)) } function O() { return y.index.get() } const V = { canScrollNext: function () { return y.index.add(1).get() !== O() }, canScrollPrev: function () { return y.index.add(-1).get() !== O() }, containerNode: function () { return b }, internalEngine: function () { return y }, destroy: function () { w || (w = !0, s.clear(), F(), a.emit("destroy"), a.clear()) }, off: m, on: p, emit: h, plugins: function () { return v }, previousScrollSnap: function () { return y.indexPrevious.get() }, reInit: x, rootNode: function () { return n }, scrollNext: function (n) { T(y.index.add(1).get(), n, -1) }, scrollPrev: function (n) { T(y.index.add(-1).get(), n, 1) }, scrollProgress: function () { return y.scrollProgress.get(y.offsetLocation.get()) }, scrollSnapList: function () { return y.scrollSnapList }, scrollTo: T, selectedScrollSnap: O, slideNodes: function () { return S }, slidesInView: function () { return y.slidesInView.get() }, slidesNotInView: function () { return y.slidesInView.get(!1) } }; return M(e, r), setTimeout((() => a.emit("init")), 0), V } return k.globalOptions = void 0, k }));



!function (n, t) { "object" == typeof exports && "undefined" != typeof module ? module.exports = t() : "function" == typeof define && define.amd ? define(t) : (n = "undefined" != typeof globalThis ? globalThis : n || self).EmblaCarouselFade = t() }(this, (function () { "use strict"; function n(n, t, e) { return Math.min(Math.max(n, t), e) } function t(n) { return "number" == typeof n && !isNaN(n) } function e(e = {}) { const o = 1, i = 0, r = .68; let s, l, c, a, f = [], d = 0, u = 0, p = 0, g = !1; function y() { b(s.selectedScrollSnap(), o) } function E() { g = !1 } function S() { g = !1, d = 0, u = 0 } function h() { const n = s.internalEngine().scrollBody.duration(); u = n ? 0 : o, g = !0, n || y() } function m(n) { const { scrollSnaps: e, location: o, target: i } = s.internalEngine(); !t(n) || f[n] < .5 || (o.set(e[n]), i.set(o)) } function b(t, e) { s.scrollSnapList().forEach(((r, l) => { const c = Math.abs(e), a = f[l], u = l === t, y = n(u ? a + c : a - c, i, o); f[l] = y; const E = u && g, S = s.previousScrollSnap(); E && (f[S] = 1 - y), u && function (n, t) { const { index: e, dragHandler: o, scrollSnaps: i } = s.internalEngine(), r = o.pointerDown(), l = 1 / (i.length - 1); let c = n, a = r ? s.selectedScrollSnap() : s.previousScrollSnap(); if (r && c === a) { const n = -1 * Math.sign(d); c = a, a = e.clone().set(a).add(n).get() } const f = a * l, u = (c - a) * l; p = f + u * t }(t, y), function (n) { const t = s.internalEngine().slideRegistry[n], { scrollSnaps: e, containerRect: o } = s.internalEngine(), r = f[n]; t.forEach((t => { const l = s.slideNodes()[t].style, c = parseFloat(r.toFixed(2)), a = c > i, f = function (n) { const { axis: t } = s.internalEngine(); return `translate${t.scroll.toUpperCase()}(${t.direction(n)}px)` }(a ? e[n] : o.width + 2); a && (l.transform = f), l.opacity = c.toString(), l.pointerEvents = r > .5 ? "auto" : "none", a || (l.transform = f) })) }(l) })) } function v() { const { dragHandler: n, index: t, scrollBody: e } = s.internalEngine(), o = s.selectedScrollSnap(); if (!n.pointerDown()) return o; const i = Math.sign(e.velocity()), r = Math.sign(d), l = t.clone().set(o).add(-1 * i).get(); return i && r ? r === i ? l : o : null } function x() { const { target: n, location: e } = s.internalEngine(), i = n.get() - e.get(), c = Math.abs(i) >= 1, a = v(), p = !t(a); return function (n) { const { dragHandler: e, scrollBody: i } = n.internalEngine(), s = e.pointerDown(), c = i.velocity(), a = i.duration(), p = v(), g = !t(p); if (s) { if (!c) return; d += c, u = Math.abs(c / l), m(p) } if (!s) { if (!a || g) return; u += (o - f[p]) / a, u *= r } g || b(p, u) }(s), !p && !c && f[a] > .999 } function M() { return p } return { name: "fade", options: e, init: function (t) { s = t; const e = s.selectedScrollSnap(), { scrollBody: r, containerRect: d, axis: u } = s.internalEngine(), p = u.measureSize(d); l = n(.75 * p, 200, 500), g = !1, f = s.scrollSnapList().map(((n, t) => t === e ? o : i)), c = r.settled, a = s.scrollProgress, r.settled = x, s.scrollProgress = M, s.on("select", h).on("slideFocus", y).on("pointerDown", S).on("pointerUp", E), function () { const { translate: n, slideLooper: t } = s.internalEngine(); n.clear(), n.toggleActive(!1), t.loopPoints.forEach((({ translate: n }) => { n.clear(), n.toggleActive(!1) })) }(), y() }, destroy: function () { const { scrollBody: n } = s.internalEngine(); n.settled = c, s.scrollProgress = a, s.off("select", h).off("slideFocus", y).off("pointerDown", S).off("pointerUp", E), s.slideNodes().forEach((n => { const t = n.style; t.opacity = "", t.transform = "", t.pointerEvents = "", n.getAttribute("style") || n.removeAttribute("style") })) } } } return e.globalOptions = void 0, e }));



try {
        let wrapper = document.querySelector('[data-bnode="beae-iqohh8"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-iqohh8",
              env = "live";
            const debounce = (fn, wait) => {
 let timeout;
 return function(...args) {
 clearTimeout(timeout);
 timeout = setTimeout(() => fn.apply(this, args), wait);
 };
};


function setupMarquee(wrapperEl, trackEl) {
 if (!trackEl) return;

 let currentDirection = wrapperEl.dataset.direction === 'right' ? 'right' : 'left';
 let isUpdating = false;
 let cloneTimeout;

 function getCleanClone(node) {
 if (!node) return null;
 const clone = node.cloneNode(true);
 clone.querySelectorAll('style, script').forEach(s => s.remove());
 return clone;
 }

 // Hàm này sẽ được gọi ngay lập tức khi hướng thay đổi
 function applyAnimation() {
 const animName = currentDirection === 'right' ? 'marquee-right' : 'marquee-left';
 // Reset để trình duyệt nhận diện animation mới
 trackEl.style.animationName = 'none';
 trackEl.style.transform = 'translateX(0)';
 
 // Trigger reflow
 void trackEl.offsetWidth;
 
 trackEl.style.animationName = animName;
 trackEl.style.animationPlayState = 'running';
 }

 function buildClones() {
 const contentEl = trackEl.querySelector('.beae-1kwker-content');
 if (!contentEl) return;

 // Xóa clone cũ
 trackEl.querySelectorAll('[data-is-full-clone]').forEach(n => n.remove());
 contentEl.querySelectorAll('[data-cloned]').forEach(n => n.remove());

 const originals = Array.from(contentEl.children).filter(
 n => !n.dataset.cloned && !n.dataset.isFullClone
 );
 if (originals.length === 0) return;

 const viewportWidth = wrapperEl.offsetWidth || window.innerWidth;
 let iterations = 0;
 
 // Fill nội dung cho đến khi đủ dài (ít nhất 2.5 lần viewport)
 while (contentEl.scrollWidth < viewportWidth * 2.5 && iterations < 20) {
 const frag = document.createDocumentFragment();
 originals.forEach(node => {
 const clone = getCleanClone(node);
 clone.dataset.cloned = 'true';
 frag.appendChild(clone);
 });
 contentEl.appendChild(frag);
 iterations++;
 }

 // Tạo bản sao toàn bộ track để chạy vô tận
 const fullClone = getCleanClone(contentEl);
 fullClone.setAttribute('aria-hidden', 'true');
 fullClone.dataset.isFullClone = 'true';
 trackEl.appendChild(fullClone);
 }

 function runClone(shouldApplyAnim = true) {
 clearTimeout(cloneTimeout);
 cloneTimeout = setTimeout(() => {
 if (isUpdating) return;
 isUpdating = true;
 
 contentObserver.disconnect();
 buildClones();
 
 // Chỉ áp dụng lại animation nếu không phải do đổi hướng (tránh bị reset 2 lần)
 if (shouldApplyAnim) applyAnimation();

 contentObserver.observe(trackEl, {
 childList: true,
 subtree: true,
 characterData: true
 });

 setTimeout(() => { isUpdating = false; }, 300);
 }, 50); // Giảm delay xuống 50ms để nhanh hơn
 }

 // Observer theo dõi nội dung bên trong (thêm/xóa phần tử)
 const contentObserver = new MutationObserver(debounce((mutations) => {
 if (isUpdating) return;
 const isSignificant = mutations.some(m => {
 const target = m.target?.nodeType === 1 ? m.target : m.target?.parentElement;
 if (target?.closest?.('[data-cloned], [data-is-full-clone]')) return false;
 return true;
 });
 if (isSignificant) runClone(true);
 }, 300));

 // Pause/Play khi hover
 const pause = () => trackEl.style.animationPlayState = 'paused';
 const play = () => trackEl.style.animationPlayState = 'running';
 wrapperEl.addEventListener('pointerenter', pause);
 wrapperEl.addEventListener('pointerleave', play);

 // QUAN TRỌNG: Theo dõi attribute direction
 const dirObserver = new MutationObserver(() => {
 const newDir = wrapperEl.dataset.direction;
 if (newDir !== currentDirection) {
 currentDirection = newDir;
 
 // 1. Chạy animation ngay lập tức
 applyAnimation();
 
 // 2. Cập nhật lại clone sau (không cần applyAnim lại trong runClone)
 isUpdating = false;
 runClone(false);
 }
 });

 dirObserver.observe(wrapperEl, { attributes: true, attributeFilter: ['data-direction'] });
 contentObserver.observe(trackEl, { childList: true, subtree: true, characterData: true });

 // Khởi tạo lần đầu
 runClone(true);

 const onResize = debounce(() => runClone(true), 400);
 window.addEventListener('resize', onResize);

 return {
 update: () => runClone(true),
 dispose: () => {
 dirObserver.disconnect();
 contentObserver.disconnect();
 clearTimeout(cloneTimeout);
 window.removeEventListener('resize', onResize);
 wrapperEl.removeEventListener('pointerenter', pause);
 wrapperEl.removeEventListener('pointerleave', play);
 }
 };
}

// Khởi tạo thực tế
const targetWrapper = typeof wrapper !== 'undefined' ? wrapper : document.querySelector('.beae-1kwker-wrapper');
const targetTrack = targetWrapper?.querySelector('.beae-1kwker-track');

if (targetWrapper && targetTrack) {
 const marquee = setupMarquee(targetWrapper, targetTrack);
}
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-0v8yul"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-0v8yul",
              env = "live";
            window.beae_slider(wrapper);
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-tk3qsb"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-tk3qsb",
              env = "live";
            
function handleHeightThumbs() {
    const mediaMain = wrapper.querySelector('.beae-product-media-main');
    const thumbSliderItems = wrapper.querySelector('.beae-thumb-wrapper .beae-slider-items');

    if (!mediaMain || !thumbSliderItems) return;

    const updateHeightThumbs = () => {
        const h = mediaMain.getBoundingClientRect().height;
        thumbSliderItems.style.maxHeight = `${h}px`;
    };

    const resizeObserver = new ResizeObserver(updateHeightThumbs);
    resizeObserver.observe(mediaMain);

    window.addEventListener('resize', updateHeightThumbs);

    updateHeightThumbs();
}


setTimeout(() => {
    handleHeightThumbs();

    window.beae_slider(wrapper);
}, 1200);
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-dke5e3"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-dke5e3",
              env = "live";
            window.beae_slider(wrapper);
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-m2q7pj"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-m2q7pj",
              env = "live";
            let rawFormat = "$" + "{{amount}} USD";
rawFormat = rawFormat.replace(/&quot;/g, '"').replace(/^"|"$/g, "");
const moneyFormat = rawFormat;
const currency = "USD";

function normalizeToCents(value) {
    if (value == null) return 0;
    if (typeof value === "string") {
        if (value.includes(".")) {
            return Math.round(parseFloat(value) * 100);
        }
        return parseInt(value, 10);
    }
    if (value < 1000 && value % 1 !== 0) {
        return Math.round(value * 100);
    }
    return value;
}

function formatMoney(value, format = moneyFormat) {
    let cents = normalizeToCents(value);
    function formatWithDelimiters(
        number,
        precision = 2,
        thousands = ",",
        decimal = "."
    ) {
        number = number.toFixed(precision);
        const parts = number.split(".");
        const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        const centsPart = parts[1] ? decimal + parts[1] : "";
        return dollars + centsPart;
    }
    return format.replace(/{{\s*(\w+)\s*}}/g, (_, token) => {
        switch (token) {
            case "amount":
                return formatWithDelimiters(cents / 100, 2);
            case "amount_no_decimals":
                return formatWithDelimiters(cents / 100, 0);
            case "amount_with_comma_separator":
                return formatWithDelimiters(cents / 100, 2, ".", ",");
            case "amount_no_decimals_with_comma_separator":
                return formatWithDelimiters(cents / 100, 0, ".", ",");
            case "currency":
                return currency;
            default:
                return formatWithDelimiters(cents / 100, 2);
        }
    });
}

function init() {
    const productEl = wrapper.querySelector(".product-json-data");
    if (!productEl) return;

    let productData = {};
    let currentVariant = null;

    try {
        const raw = JSON.parse(productEl.textContent || "{}");
        function normalizeVariant(v) {
            let price;
            let compareAtPrice;
            if (env === "editor") {
                price = Math.round(Number(v.price || 0) * 100);
                compareAtPrice = Math.round(Number(v.compareAtPrice || 0) * 100);
            } else {
                price = Number(v.price || 0);
                compareAtPrice = Number(v.compare_at_price || 0);
            }
            return {
                id: typeof v.id === "string" ? v.id.split("/").pop() : v.id,
                price,
                compareAtPrice,
                inventoryQuantity: v.inventoryQuantity ?? v.inventory_quantity ?? 0,
                availableForSale: v.availableForSale ?? v.available ?? false,
                options:
                    v.options ||
                    (v.selectedOptions
                        ? v.selectedOptions.map((o) => o.value)
                        : [v.option1, v.option2, v.option3].filter(Boolean)),
                image: { url: v.image?.url || v.featured_image?.src },
                sku: v.sku || "",
            };
        }

        let variants = [];
        if (Array.isArray(raw?.variants)) {
            variants = raw.variants;
        } else if (Array.isArray(raw?.variants?.edges)) {
            variants = raw.variants.edges.map((e) => e.node);
        }
        productData = { ...raw, variants: variants.map(normalizeVariant) };
    } catch (e) {
        console.error("JSON parse error", e);
        return;
    }

    const variantSelect = wrapper.querySelector("[data-beae-variant-select]");
    const form = wrapper.closest('form[action="/cart/add"]');
    const idInput = form?.querySelector("input[name='id']");
    const qtyInput = wrapper.querySelector(".beae-qty-input");
    const warning = wrapper.querySelector(".beae-qty-warning");

    const normalize = (v) =>
        String(v || "")
            .trim()
            .toLowerCase();

    function getSelectedOptions() {
        const arr = [];
        wrapper.querySelectorAll(".beae-option").forEach((optEl, i) => {
            const radio = optEl.querySelector("input.beae-radio:checked");
            const select = optEl.querySelector(".beae-swatch-sel");
            arr[i] = radio?.value || select?.value || "";
        });
        return arr;
    }

    function findVariant(selected) {
        const selectedNorm = selected.map(normalize);
        return productData.variants.find((v) => {
            const values = (v.options || []).map(normalize);
            return values.every((val, i) => val === selectedNorm[i]);
        });
    }

    function updatePrice(v) {
        const priceEl = wrapper.querySelector(".beae-product-price");
        const compareEl = wrapper.querySelector(".beae-product-price-compare");
        const wrap = wrapper.querySelector(".beae-product-price-wrapper");
        if (!priceEl) return;
        const isContact = v.price <= 0;
        const onSale = !isContact && v.compareAtPrice > v.price;
        if (isContact) {
            priceEl.textContent = "Contact";
            if (compareEl) compareEl.style.display = "none";
            wrap?.classList.remove("beae-on-sale");
            return;
        }
        priceEl.textContent = formatMoney(v.price, moneyFormat);
        if (onSale) {
            compareEl.textContent = formatMoney(v.compareAtPrice, moneyFormat);
            compareEl.style.display = "inline";
            wrap?.classList.add("beae-on-sale");
        } else {
            compareEl.style.display = "none";
            wrap?.classList.remove("beae-on-sale");
        }
    }

    function updateInventory(v) {
        const el = wrapper.querySelector(".beae-inventory");
        if (!el) return;
        const qty = Number(v.inventoryQuantity || 0);
        const available = v.availableForSale;
        const low = Number(el.dataset.low || 0);
        const critical = Number(el.dataset.critical || 0);
        const max = Number(el.dataset.max || 100);
        const stateMap = {
            OUT: !available || qty <= 0,
            CRITICAL: qty > 0 && qty <= critical,
            LOW: qty > critical && qty <= low,
            IN: qty > low,
        };
        let currentState = "IN";
        if (stateMap.OUT) currentState = "OUT";
        else if (stateMap.CRITICAL) currentState = "CRITICAL";
        else if (stateMap.LOW) currentState = "LOW";

        const stateClassMap = {
            OUT: "beae-inventory-out",
            CRITICAL: "beae-inventory-critical",
            LOW: "beae-inventory-low",
            IN: "beae-inventory-in",
        };
        el.classList.remove(
            "beae-inventory-out",
            "beae-inventory-critical",
            "beae-inventory-low",
            "beae-inventory-in"
        );
        el.classList.add(stateClassMap[currentState]);

        const visibilityMap = {
            instock: currentState === "IN",
            lowstock: currentState === "LOW",
            criticalstock: currentState === "CRITICAL" || currentState === "OUT",
        };
        el.querySelectorAll("[data-beae-inventory]").forEach((child) => {
            const type = child.dataset.beaeInventory;
            child.classList.toggle("beae-show", !!visibilityMap[type]);
            const template = child.dataset.template || "";
            if (template.includes("{stock}")) {
                child.textContent = template.replace(/\{stock\}/g, qty);
            }
        });
        const bar = el.querySelector(".beae-inventory-progress-bar");
        if (bar) {
            if (stateMap.OUT) {
                bar.style.width = "0%";
            } else {
                const percent = max > 0 ? Math.min(100, (qty / max) * 100) : 0;
                bar.style.width = percent + "%";
            }
        }
    }

    function updateSKU(v) {
        const skuEl = wrapper.querySelector(".beae-sku");
        if (!skuEl) return;
        const sku = v?.sku || "";
        skuEl.textContent = "SKU: " + sku;
    }

    function updateButton(v) {
        const buttons = wrapper.querySelectorAll(".beae-atc-btn, .beae-co-btn");
        buttons.forEach(btn => {
            const isCheckout = btn.classList.contains('beae-co-btn');
            const price = v.price;
            const available = v.availableForSale;
            const isSoldOut = !available;
            const isContact = price <= 0;
            let state = isCheckout ? "co" : "atc";
            let label = isCheckout ? (btn.dataset.coText || "Checkout") : (btn.dataset.atcText || "Add to cart");

            if (isSoldOut) {
                state = "soldout";
                label = btn.dataset.soldOutText || "Sold out";
            } else if (isContact) {
                state = "contact";
                label = btn.dataset.contactText || "Contact";
            }
            btn.classList.remove(
                "beae-btn-atc", "beae-btn-co",
                "beae-btn-soldout", "beae-btn-contact"
            );
            btn.classList.add("beae-btn-" + state);
            btn.dataset.state = state;
            const textEl = btn.querySelector(".beae-atc-text, .beae-checkout-text");
            if (textEl) textEl.textContent = label;
            btn.disabled = isSoldOut || isContact;
        });
    }

    function initCheckoutEvent() {
        const btn = wrapper.querySelector(".beae-co-btn");
        if (!btn) return;
        btn.addEventListener("click", function (e) {
            e.preventDefault();
            if (btn.disabled) return;
            const form = btn.closest('form[action="/cart/add"]');
            if (!form) return;
            let returnTo = form.querySelector('input[name="return_to"]');
            if (!returnTo) {
                returnTo = document.createElement('input');
                returnTo.type = 'hidden';
                returnTo.name = 'return_to';
                form.appendChild(returnTo);
            }
            returnTo.value = '/checkout';
            form.submit();
        });
    }

    function updateMedia() {

        const media = wrapper.querySelector(".beae-media-wrapper");

        if (!media || !media.embla) return;

        const imgs = media.querySelectorAll(".beae-product-image");

        const activeInput = wrapper.querySelector(
            ".beae-radio:checked, .beae-swatch-sel"
        );

        let activeImage = "";

        if (activeInput?.matches(".beae-radio")) {
            activeImage = activeInput.dataset.srcImage || "";
        }

        if (
            activeInput?.matches(".beae-swatch-sel")
        ) {
            activeImage =
                activeInput.options[activeInput.selectedIndex]
                    ?.dataset.srcImage || "";
        }

        if (!activeImage) return;

        function getImageId(url) {
            try {
                const file = new URL(url, location.origin)
                    .pathname
                    .split("/")
                    .pop();

                return file.match(/[a-f0-9-]{36}/)?.[0] || file;
            } catch {
                return "";
            }
        }

        const target = getImageId(activeImage);

        const index = [...imgs].findIndex((img) => {
            const current = getImageId(img.src);

            return current === target;
        });

        if (index !== -1) {

            media.embla.scrollTo(index);
        } else {
            console.log("NO IMAGE MATCH");
        }
    }

    function bindQtyWarning(v) {
        const qtyInput = wrapper.querySelector(".beae-qty-input");
        const warning = wrapper.querySelector(".beae-qty-warning");
        const form = wrapper.closest("form");
        const btn = wrapper.querySelector(".beae-atc-btn");
        if (!qtyInput || !warning || !form) return;
        currentVariant = v;
        const getStock = () => Number(currentVariant?.inventoryQuantity || 0);
        const getTemplate = () => {
            if (!warning.dataset.template) {
                warning.dataset.template = warning.innerHTML;
            }
            return warning.dataset.template;
        };
        const showError = (stock) => {
            warning.style.display = "block";
            warning.innerHTML = getTemplate().replace(/\{stock\}/g, stock);
            wrapper.querySelectorAll(".beae-atc-btn, .beae-co-btn").forEach((b) => {
                b.disabled = true;
            });
        };
        const hideError = () => {
            if (warning.style.display === "none") return;
            warning.style.display = "none";
            updateButton(currentVariant);
        };

        const validate = () => {
            const qty = parseInt(qtyInput.value, 10) || 0;
            const stock = getStock();
            if (qty > stock) {
                showError(stock);
                return false;
            } else {
                hideError();
                return true;
            }
        };

        hideError();

        if (btn && !btn._qtyBound) {
            btn.addEventListener(
                "click",
                (e) => {
                    if (!validate()) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                    }
                },
                true
            );
            btn._qtyBound = true;
        }
        if (!form._qtyBound) {
            form.addEventListener(
                "submit",
                (e) => {
                    if (!validate()) {
                        e.preventDefault();
                        e.stopImmediatePropagation();
                    }
                },
                true
            );
            form._qtyBound = true;
        }
        if (!qtyInput._qtyBound) {
            qtyInput.addEventListener("input", () => {
                hideError();
            });
            qtyInput._qtyBound = true;
        }
    }

    function apply(v) {
        if (!v) return;
        currentVariant = v;
        if (idInput) idInput.value = v.id;
        if (variantSelect) variantSelect.value = v.id;
        updatePrice(v);
        updateInventory(v);
        updateSKU(v);
        updateButton(v);

        bindQtyWarning(v);

        // setTimeout(() => {            
        //     updateMedia(v);
        // }, 50);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                updateMedia();
            });
        });
    }


    function initVariant() {
        const selected = getSelectedOptions();
        let v = findVariant(selected);
        if (!v) {
            v =
                productData.variants.find((x) => x.availableForSale) ||
                productData.variants[0];
        }
        apply(v);
    }
    initCheckoutEvent();
    initVariant();

    if (!wrapper.dataset.variantInit) {
        wrapper.dataset.variantInit = "1";

        wrapper.addEventListener("change", (e) => {
            const t = e.target;

            if (t.matches(".beae-radio") || t.matches(".beae-swatch-sel")) {

                if (t.type === "radio" && !t.checked) return;

                const selected = getSelectedOptions();

                const v = findVariant(selected);

                if (v) apply(v);
            }
        });
    }
}

// setTimeout(init, 800);

function waitForReady() {
    const productEl = wrapper.querySelector(".product-json-data");
    const media = wrapper.querySelector(".beae-media-wrapper");

    if (!productEl || !media?.embla) {
        requestAnimationFrame(waitForReady);
        return;
    }

    init();
}

waitForReady();
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-xptm6k"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-xptm6k",
              env = "live";
            const debounce = (fn, wait) => {
 let timeout;
 return function(...args) {
 clearTimeout(timeout);
 timeout = setTimeout(() => fn.apply(this, args), wait);
 };
};


function setupMarquee(wrapperEl, trackEl) {
 if (!trackEl) return;

 let currentDirection = wrapperEl.dataset.direction === 'right' ? 'right' : 'left';
 let isUpdating = false;
 let cloneTimeout;

 function getCleanClone(node) {
 if (!node) return null;
 const clone = node.cloneNode(true);
 clone.querySelectorAll('style, script').forEach(s => s.remove());
 return clone;
 }

 // Hàm này sẽ được gọi ngay lập tức khi hướng thay đổi
 function applyAnimation() {
 const animName = currentDirection === 'right' ? 'marquee-right' : 'marquee-left';
 // Reset để trình duyệt nhận diện animation mới
 trackEl.style.animationName = 'none';
 trackEl.style.transform = 'translateX(0)';
 
 // Trigger reflow
 void trackEl.offsetWidth;
 
 trackEl.style.animationName = animName;
 trackEl.style.animationPlayState = 'running';
 }

 function buildClones() {
 const contentEl = trackEl.querySelector('.beae-1kwker-content');
 if (!contentEl) return;

 // Xóa clone cũ
 trackEl.querySelectorAll('[data-is-full-clone]').forEach(n => n.remove());
 contentEl.querySelectorAll('[data-cloned]').forEach(n => n.remove());

 const originals = Array.from(contentEl.children).filter(
 n => !n.dataset.cloned && !n.dataset.isFullClone
 );
 if (originals.length === 0) return;

 const viewportWidth = wrapperEl.offsetWidth || window.innerWidth;
 let iterations = 0;
 
 // Fill nội dung cho đến khi đủ dài (ít nhất 2.5 lần viewport)
 while (contentEl.scrollWidth < viewportWidth * 2.5 && iterations < 20) {
 const frag = document.createDocumentFragment();
 originals.forEach(node => {
 const clone = getCleanClone(node);
 clone.dataset.cloned = 'true';
 frag.appendChild(clone);
 });
 contentEl.appendChild(frag);
 iterations++;
 }

 // Tạo bản sao toàn bộ track để chạy vô tận
 const fullClone = getCleanClone(contentEl);
 fullClone.setAttribute('aria-hidden', 'true');
 fullClone.dataset.isFullClone = 'true';
 trackEl.appendChild(fullClone);
 }

 function runClone(shouldApplyAnim = true) {
 clearTimeout(cloneTimeout);
 cloneTimeout = setTimeout(() => {
 if (isUpdating) return;
 isUpdating = true;
 
 contentObserver.disconnect();
 buildClones();
 
 // Chỉ áp dụng lại animation nếu không phải do đổi hướng (tránh bị reset 2 lần)
 if (shouldApplyAnim) applyAnimation();

 contentObserver.observe(trackEl, {
 childList: true,
 subtree: true,
 characterData: true
 });

 setTimeout(() => { isUpdating = false; }, 300);
 }, 50); // Giảm delay xuống 50ms để nhanh hơn
 }

 // Observer theo dõi nội dung bên trong (thêm/xóa phần tử)
 const contentObserver = new MutationObserver(debounce((mutations) => {
 if (isUpdating) return;
 const isSignificant = mutations.some(m => {
 const target = m.target?.nodeType === 1 ? m.target : m.target?.parentElement;
 if (target?.closest?.('[data-cloned], [data-is-full-clone]')) return false;
 return true;
 });
 if (isSignificant) runClone(true);
 }, 300));

 // Pause/Play khi hover
 const pause = () => trackEl.style.animationPlayState = 'paused';
 const play = () => trackEl.style.animationPlayState = 'running';
 wrapperEl.addEventListener('pointerenter', pause);
 wrapperEl.addEventListener('pointerleave', play);

 // QUAN TRỌNG: Theo dõi attribute direction
 const dirObserver = new MutationObserver(() => {
 const newDir = wrapperEl.dataset.direction;
 if (newDir !== currentDirection) {
 currentDirection = newDir;
 
 // 1. Chạy animation ngay lập tức
 applyAnimation();
 
 // 2. Cập nhật lại clone sau (không cần applyAnim lại trong runClone)
 isUpdating = false;
 runClone(false);
 }
 });

 dirObserver.observe(wrapperEl, { attributes: true, attributeFilter: ['data-direction'] });
 contentObserver.observe(trackEl, { childList: true, subtree: true, characterData: true });

 // Khởi tạo lần đầu
 runClone(true);

 const onResize = debounce(() => runClone(true), 400);
 window.addEventListener('resize', onResize);

 return {
 update: () => runClone(true),
 dispose: () => {
 dirObserver.disconnect();
 contentObserver.disconnect();
 clearTimeout(cloneTimeout);
 window.removeEventListener('resize', onResize);
 wrapperEl.removeEventListener('pointerenter', pause);
 wrapperEl.removeEventListener('pointerleave', play);
 }
 };
}

// Khởi tạo thực tế
const targetWrapper = typeof wrapper !== 'undefined' ? wrapper : document.querySelector('.beae-1kwker-wrapper');
const targetTrack = targetWrapper?.querySelector('.beae-1kwker-track');

if (targetWrapper && targetTrack) {
 const marquee = setupMarquee(targetWrapper, targetTrack);
}
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-ec64u4"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-ec64u4",
              env = "live";
            window.beae_slider(wrapper);
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-jp359m"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-jp359m",
              env = "live";
            const debounce = (fn, wait) => {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
    };
};

function setupMarquee(wrapperEl, trackEl) {
    if (!trackEl) return;

    let currentDirection = wrapperEl.dataset.direction || 'left';
    let isUpdating = false;
    let cloneTimeout;

    const isVertical = () =>
        currentDirection === 'top' || currentDirection === 'bottom';

    function getCleanClone(node) {
        if (!node) return null;

        const clone = node.cloneNode(true);

        clone.querySelectorAll('style, script').forEach(s => s.remove());

        return clone;
    }

    function applyAnimation() {
        let animName = 'marquee-left';

        switch (currentDirection) {
            case 'right':
                animName = 'marquee-right';
                break;

            case 'top':
                animName = 'marquee-top';
                break;

            case 'bottom':
                animName = 'marquee-bottom';
                break;
        }

        trackEl.style.animationName = 'none';
        trackEl.style.transform = 'translate(0,0)';

        void trackEl.offsetWidth;

        trackEl.style.animationName = animName;
        trackEl.style.animationPlayState = 'running';

        trackEl.style.flexDirection = isVertical()
            ? 'column'
            : 'row';
    }

    function buildClones() {
        const contentEl = trackEl.querySelector('.beae-3tg2vw-content');
        if (!contentEl) return;

        trackEl.querySelectorAll('[data-is-full-clone]').forEach(n => n.remove());
        contentEl.querySelectorAll('[data-cloned]').forEach(n => n.remove());

        const originals = Array.from(contentEl.children).filter(
            n => !n.dataset.cloned && !n.dataset.isFullClone
        );
        if (originals.length === 0) return;

        // Với vertical: dùng height thực của wrapper, fallback về 400px (không dùng window.innerHeight)
        const viewportSize = isVertical()
            ? (wrapperEl.offsetHeight || 400)   // ← đổi fallback
            : (wrapperEl.offsetWidth || window.innerWidth);

        let iterations = 0;
        const MAX_ITER = 7; // ← giảm từ 20 xuống 5

        while (
            (isVertical() ? contentEl.scrollHeight : contentEl.scrollWidth) < viewportSize * 2.5 &&
            iterations < MAX_ITER
        ) {
            const frag = document.createDocumentFragment();
            originals.forEach(node => {
                const clone = getCleanClone(node);
                clone.dataset.cloned = 'true';
                frag.appendChild(clone);
            });
            contentEl.appendChild(frag);
            iterations++;
        }

        const fullClone = getCleanClone(contentEl);
        fullClone.setAttribute('aria-hidden', 'true');
        fullClone.dataset.isFullClone = 'true';
        trackEl.appendChild(fullClone);
    }

    function runClone(shouldApplyAnim = true) {
        clearTimeout(cloneTimeout);

        cloneTimeout = setTimeout(() => {
            if (isUpdating) return;

            isUpdating = true;

            contentObserver.disconnect();

            buildClones();

            if (shouldApplyAnim) {
                applyAnimation();
            }

            contentObserver.observe(trackEl, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true
            });

            setTimeout(() => {
                isUpdating = false;
            }, 300);

        }, 50);
    }

    const contentObserver = new MutationObserver(
        debounce((mutations) => {
            if (isUpdating) return;

            const isSignificant = mutations.some(m => {
                const target =
                    m.target?.nodeType === 1
                        ? m.target
                        : m.target?.parentElement;

                if (target?.closest?.('[data-cloned], [data-is-full-clone]')) {
                    return false;
                }

                if (m.type === 'attributes' && m.attributeName === 'style') {
                    return false;
                }

                return true;
            });

            if (isSignificant) {
                runClone(true);
            }

        }, 300)
    );

    const pause = () => {
        trackEl.style.animationPlayState = 'paused';
    };

    const play = () => {
        trackEl.style.animationPlayState = 'running';
    };

    wrapperEl.addEventListener('pointerenter', pause);

    wrapperEl.addEventListener('pointerleave', play);

    const dirObserver = new MutationObserver(() => {
        const newDir = wrapperEl.dataset.direction || 'left';

        if (newDir !== currentDirection) {
            currentDirection = newDir;

            applyAnimation();

            isUpdating = false;

            runClone(false);
        }
    });

    dirObserver.observe(wrapperEl, {
        attributes: true,
        attributeFilter: ['data-direction']
    });

    contentObserver.observe(trackEl, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
    });

    runClone(true);

    const onResize = debounce(() => runClone(true), 400);

    window.addEventListener('resize', onResize);

    return {
        update: () => runClone(true),

        dispose: () => {
            dirObserver.disconnect();

            contentObserver.disconnect();

            clearTimeout(cloneTimeout);

            window.removeEventListener('resize', onResize);

            wrapperEl.removeEventListener('pointerenter', pause);

            wrapperEl.removeEventListener('pointerleave', play);
        }
    };
}

const targetWrapper = typeof wrapper !== 'undefined'
    ? wrapper
    : document.querySelector('.beae-3tg2vw-wrapper');

const targetTrack = targetWrapper?.querySelector('.beae-3tg2vw-track');

if (targetWrapper && targetTrack) {
    setupMarquee(targetWrapper, targetTrack);
}
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-ital7u"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-ital7u",
              env = "live";
            const debounce = (fn, wait) => {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
    };
};

function setupMarquee(wrapperEl, trackEl) {
    if (!trackEl) return;

    let currentDirection = wrapperEl.dataset.direction || 'left';
    let isUpdating = false;
    let cloneTimeout;

    const isVertical = () =>
        currentDirection === 'top' || currentDirection === 'bottom';

    function getCleanClone(node) {
        if (!node) return null;

        const clone = node.cloneNode(true);

        clone.querySelectorAll('style, script').forEach(s => s.remove());

        return clone;
    }

    function applyAnimation() {
        let animName = 'marquee-left';

        switch (currentDirection) {
            case 'right':
                animName = 'marquee-right';
                break;

            case 'top':
                animName = 'marquee-top';
                break;

            case 'bottom':
                animName = 'marquee-bottom';
                break;
        }

        trackEl.style.animationName = 'none';
        trackEl.style.transform = 'translate(0,0)';

        void trackEl.offsetWidth;

        trackEl.style.animationName = animName;
        trackEl.style.animationPlayState = 'running';

        trackEl.style.flexDirection = isVertical()
            ? 'column'
            : 'row';
    }

    function buildClones() {
        const contentEl = trackEl.querySelector('.beae-3tg2vw-content');
        if (!contentEl) return;

        trackEl.querySelectorAll('[data-is-full-clone]').forEach(n => n.remove());
        contentEl.querySelectorAll('[data-cloned]').forEach(n => n.remove());

        const originals = Array.from(contentEl.children).filter(
            n => !n.dataset.cloned && !n.dataset.isFullClone
        );
        if (originals.length === 0) return;

        // Với vertical: dùng height thực của wrapper, fallback về 400px (không dùng window.innerHeight)
        const viewportSize = isVertical()
            ? (wrapperEl.offsetHeight || 400)   // ← đổi fallback
            : (wrapperEl.offsetWidth || window.innerWidth);

        let iterations = 0;
        const MAX_ITER = 7; // ← giảm từ 20 xuống 5

        while (
            (isVertical() ? contentEl.scrollHeight : contentEl.scrollWidth) < viewportSize * 2.5 &&
            iterations < MAX_ITER
        ) {
            const frag = document.createDocumentFragment();
            originals.forEach(node => {
                const clone = getCleanClone(node);
                clone.dataset.cloned = 'true';
                frag.appendChild(clone);
            });
            contentEl.appendChild(frag);
            iterations++;
        }

        const fullClone = getCleanClone(contentEl);
        fullClone.setAttribute('aria-hidden', 'true');
        fullClone.dataset.isFullClone = 'true';
        trackEl.appendChild(fullClone);
    }

    function runClone(shouldApplyAnim = true) {
        clearTimeout(cloneTimeout);

        cloneTimeout = setTimeout(() => {
            if (isUpdating) return;

            isUpdating = true;

            contentObserver.disconnect();

            buildClones();

            if (shouldApplyAnim) {
                applyAnimation();
            }

            contentObserver.observe(trackEl, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true
            });

            setTimeout(() => {
                isUpdating = false;
            }, 300);

        }, 50);
    }

    const contentObserver = new MutationObserver(
        debounce((mutations) => {
            if (isUpdating) return;

            const isSignificant = mutations.some(m => {
                const target =
                    m.target?.nodeType === 1
                        ? m.target
                        : m.target?.parentElement;

                if (target?.closest?.('[data-cloned], [data-is-full-clone]')) {
                    return false;
                }

                if (m.type === 'attributes' && m.attributeName === 'style') {
                    return false;
                }

                return true;
            });

            if (isSignificant) {
                runClone(true);
            }

        }, 300)
    );

    const pause = () => {
        trackEl.style.animationPlayState = 'paused';
    };

    const play = () => {
        trackEl.style.animationPlayState = 'running';
    };

    wrapperEl.addEventListener('pointerenter', pause);

    wrapperEl.addEventListener('pointerleave', play);

    const dirObserver = new MutationObserver(() => {
        const newDir = wrapperEl.dataset.direction || 'left';

        if (newDir !== currentDirection) {
            currentDirection = newDir;

            applyAnimation();

            isUpdating = false;

            runClone(false);
        }
    });

    dirObserver.observe(wrapperEl, {
        attributes: true,
        attributeFilter: ['data-direction']
    });

    contentObserver.observe(trackEl, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
    });

    runClone(true);

    const onResize = debounce(() => runClone(true), 400);

    window.addEventListener('resize', onResize);

    return {
        update: () => runClone(true),

        dispose: () => {
            dirObserver.disconnect();

            contentObserver.disconnect();

            clearTimeout(cloneTimeout);

            window.removeEventListener('resize', onResize);

            wrapperEl.removeEventListener('pointerenter', pause);

            wrapperEl.removeEventListener('pointerleave', play);
        }
    };
}

const targetWrapper = typeof wrapper !== 'undefined'
    ? wrapper
    : document.querySelector('.beae-3tg2vw-wrapper');

const targetTrack = targetWrapper?.querySelector('.beae-3tg2vw-track');

if (targetWrapper && targetTrack) {
    setupMarquee(targetWrapper, targetTrack);
}
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };try {
        let wrapper = document.querySelector('[data-bnode="beae-z7negz"]');
        if (wrapper) {
          if (!wrapper.getAttribute('data-executed-scripts')) {
            wrapper.setAttribute('data-executed-scripts', true);
            let id = "beae-z7negz",
              env = "live";
            const debounce = (fn, wait) => {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
    };
};

function setupMarquee(wrapperEl, trackEl) {
    if (!trackEl) return;

    let currentDirection = wrapperEl.dataset.direction || 'left';
    let isUpdating = false;
    let cloneTimeout;

    const isVertical = () =>
        currentDirection === 'top' || currentDirection === 'bottom';

    function getCleanClone(node) {
        if (!node) return null;

        const clone = node.cloneNode(true);

        clone.querySelectorAll('style, script').forEach(s => s.remove());

        return clone;
    }

    function applyAnimation() {
        let animName = 'marquee-left';

        switch (currentDirection) {
            case 'right':
                animName = 'marquee-right';
                break;

            case 'top':
                animName = 'marquee-top';
                break;

            case 'bottom':
                animName = 'marquee-bottom';
                break;
        }

        trackEl.style.animationName = 'none';
        trackEl.style.transform = 'translate(0,0)';

        void trackEl.offsetWidth;

        trackEl.style.animationName = animName;
        trackEl.style.animationPlayState = 'running';

        trackEl.style.flexDirection = isVertical()
            ? 'column'
            : 'row';
    }

    function buildClones() {
        const contentEl = trackEl.querySelector('.beae-3tg2vw-content');
        if (!contentEl) return;

        trackEl.querySelectorAll('[data-is-full-clone]').forEach(n => n.remove());
        contentEl.querySelectorAll('[data-cloned]').forEach(n => n.remove());

        const originals = Array.from(contentEl.children).filter(
            n => !n.dataset.cloned && !n.dataset.isFullClone
        );
        if (originals.length === 0) return;

        // Với vertical: dùng height thực của wrapper, fallback về 400px (không dùng window.innerHeight)
        const viewportSize = isVertical()
            ? (wrapperEl.offsetHeight || 400)   // ← đổi fallback
            : (wrapperEl.offsetWidth || window.innerWidth);

        let iterations = 0;
        const MAX_ITER = 7; // ← giảm từ 20 xuống 5

        while (
            (isVertical() ? contentEl.scrollHeight : contentEl.scrollWidth) < viewportSize * 2.5 &&
            iterations < MAX_ITER
        ) {
            const frag = document.createDocumentFragment();
            originals.forEach(node => {
                const clone = getCleanClone(node);
                clone.dataset.cloned = 'true';
                frag.appendChild(clone);
            });
            contentEl.appendChild(frag);
            iterations++;
        }

        const fullClone = getCleanClone(contentEl);
        fullClone.setAttribute('aria-hidden', 'true');
        fullClone.dataset.isFullClone = 'true';
        trackEl.appendChild(fullClone);
    }

    function runClone(shouldApplyAnim = true) {
        clearTimeout(cloneTimeout);

        cloneTimeout = setTimeout(() => {
            if (isUpdating) return;

            isUpdating = true;

            contentObserver.disconnect();

            buildClones();

            if (shouldApplyAnim) {
                applyAnimation();
            }

            contentObserver.observe(trackEl, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true
            });

            setTimeout(() => {
                isUpdating = false;
            }, 300);

        }, 50);
    }

    const contentObserver = new MutationObserver(
        debounce((mutations) => {
            if (isUpdating) return;

            const isSignificant = mutations.some(m => {
                const target =
                    m.target?.nodeType === 1
                        ? m.target
                        : m.target?.parentElement;

                if (target?.closest?.('[data-cloned], [data-is-full-clone]')) {
                    return false;
                }

                if (m.type === 'attributes' && m.attributeName === 'style') {
                    return false;
                }

                return true;
            });

            if (isSignificant) {
                runClone(true);
            }

        }, 300)
    );

    const pause = () => {
        trackEl.style.animationPlayState = 'paused';
    };

    const play = () => {
        trackEl.style.animationPlayState = 'running';
    };

    wrapperEl.addEventListener('pointerenter', pause);

    wrapperEl.addEventListener('pointerleave', play);

    const dirObserver = new MutationObserver(() => {
        const newDir = wrapperEl.dataset.direction || 'left';

        if (newDir !== currentDirection) {
            currentDirection = newDir;

            applyAnimation();

            isUpdating = false;

            runClone(false);
        }
    });

    dirObserver.observe(wrapperEl, {
        attributes: true,
        attributeFilter: ['data-direction']
    });

    contentObserver.observe(trackEl, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
    });

    runClone(true);

    const onResize = debounce(() => runClone(true), 400);

    window.addEventListener('resize', onResize);

    return {
        update: () => runClone(true),

        dispose: () => {
            dirObserver.disconnect();

            contentObserver.disconnect();

            clearTimeout(cloneTimeout);

            window.removeEventListener('resize', onResize);

            wrapperEl.removeEventListener('pointerenter', pause);

            wrapperEl.removeEventListener('pointerleave', play);
        }
    };
}

const targetWrapper = typeof wrapper !== 'undefined'
    ? wrapper
    : document.querySelector('.beae-3tg2vw-wrapper');

const targetTrack = targetWrapper?.querySelector('.beae-3tg2vw-track');

if (targetWrapper && targetTrack) {
    setupMarquee(targetWrapper, targetTrack);
}
          }
        }
      } catch (ex) { console.error('Beae Editor JS Error: ' + ex.message) };